import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import _ from "lodash";

import { URLS } from "config/urls";
import { ArrowBackIcon, MoreVertIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { reverse } from "utils/urls";

import Button from "components/Button";
import ButtonDropdown from "components/ButtonDropdown";
import { MenuItem } from "components/Menu";
import PageHeader from "components/PageHeader";
import Select from "components/Select";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Text from "components/Text";
import TextField from "components/TextField";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";
import { useConfirmationDialog } from "components/useConfirmationDialog";
import { useLoadingBackdrop } from "components/useLoadingBackdrop";

import {
  companySectionAutomaticFillApprove,
  ECompanySectionAutomaticFillStatus,
  useCompanySectionAutomaticFill,
  useCompanySectionAutomaticFillFileSignedUrl,
} from "entities/Deal/sdk";
import PDFEmbed from "entities/File/components/PDFEmbed";

const DealPackageExtractedDataReview = () => {
  const params = useParams<{
    dealId: string;
    packageId: string;
    tabId: string;
    companySectionAutomaticFillId: string;
  }>();
  const dealId = Number(params.dealId);
  const packageId = Number(params.packageId);
  const tabId = Number(params.tabId);
  const companySectionAutomaticFillId = Number(
    params.companySectionAutomaticFillId
  );

  const [showLoading, hideLoading] = useLoadingBackdrop();
  const [PDFViewerFocusedQuote, setPDFViewerFocusedQuote] = useState<
    string | null
  >();

  const [reviewedData, setReviewedData] = useState<{ [key: number]: string }>(
    {}
  );
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(
    null
  );
  const [fieldsMarkedAsNA, setFieldsMarkedAsNA] = useState<number[]>([]);

  const {
    data: companySectionAutomaticFill,
    isLoading: isFetchingCompanySectionAutomaticFill,
  } = useCompanySectionAutomaticFill({ dealId, companySectionAutomaticFillId });
  const {
    data: companySectionAutomaticFillFileSignedUrl,
    isValidating: isFetchingCompanySectionAutomaticFillFileSignedUrl,
  } = useCompanySectionAutomaticFillFileSignedUrl({
    dealId,
    companySectionAutomaticFillId,
    fileId: selectedDocumentId || -1,
    enabled: !_.isNil(selectedDocumentId),
  });
  const navigate = useNavigate();
  const { show: showConfirmationDialog } = useConfirmationDialog();

  useEffect(() => {
    if (!_.isNil(companySectionAutomaticFill)) {
      setSelectedDocumentId(companySectionAutomaticFill.files[0].id);
    }
  }, [companySectionAutomaticFill]);

  useEffect(() => {
    if (!_.isNil(companySectionAutomaticFill)) {
      setReviewedData(
        companySectionAutomaticFill.field_values.reduce(
          (acc, fieldValue) => ({
            ...acc,
            [fieldValue.field.id]: fieldValue.value || "",
          }),
          {}
        )
      );
    }
  }, [companySectionAutomaticFill]);

  useEffect(() => {
    if (!_.isNil(companySectionAutomaticFill)) {
      setFieldsMarkedAsNA(
        companySectionAutomaticFill.field_values
          .filter(({ field }) => field.is_na)
          .map(({ field }) => field.id)
      );
    }
  }, [companySectionAutomaticFill]);

  const isCompanySectionAutomaticFillFinished = useMemo(
    () =>
      companySectionAutomaticFill?.status ===
      ECompanySectionAutomaticFillStatus.FINISHED,
    [companySectionAutomaticFill]
  );

  const backLink = useMemo(() => {
    const url = reverse(URLS.DEAL_PACKAGE, {
      dealId,
      packageId,
      tabId,
    });

    if (_.isNil(companySectionAutomaticFill)) {
      return url;
    }

    return `${url}?fieldId=${companySectionAutomaticFill.field.id}`;
  }, [companySectionAutomaticFill, dealId, packageId, tabId]);

  const handleBackClick = useCallback(() => {
    if (isCompanySectionAutomaticFillFinished) {
      navigate(backLink);
      return;
    }

    showConfirmationDialog({
      onConfirm: () => navigate(backLink),
      confirmButtonText: "Yes, go back",
      message: "Are you sure you want to go back? Your changes will be lost.",
    });
  }, [
    backLink,
    navigate,
    showConfirmationDialog,
    isCompanySectionAutomaticFillFinished,
  ]);

  const handleFieldNAToggle = useCallback(
    ({ fieldId }: { fieldId: number }) =>
      setFieldsMarkedAsNA((prevValue) => _.xor(prevValue, [fieldId])),
    []
  );

  const handleApprove = useCallback(() => {
    showLoading();

    const valuesToBeCreated = _.pickBy(
      reviewedData,
      (_, key) => !fieldsMarkedAsNA.includes(Number(key))
    );
    const formattedValuesToBeCreated = Object.keys(valuesToBeCreated).map(
      (fieldId) => ({
        field_id: Number(fieldId),
        value: valuesToBeCreated[fieldId],
      })
    );

    companySectionAutomaticFillApprove({
      dealId,
      companySectionAutomaticFillId,
      fieldValues: formattedValuesToBeCreated,
      fieldsToBeMarkedAsNA: fieldsMarkedAsNA,
    })
      .then(() => {
        navigate(backLink);
        toast.successMessage("Extracted data has been imported successfully!");
      })
      .finally(() => {
        hideLoading();
      });
  }, [
    reviewedData,
    fieldsMarkedAsNA,
    backLink,
    navigate,
    dealId,
    companySectionAutomaticFillId,
    showLoading,
    hideLoading,
  ]);

  const isLoading =
    _.isNil(companySectionAutomaticFill) ||
    isFetchingCompanySectionAutomaticFill ||
    _.isNil(selectedDocumentId);

  return (
    <Stack paddingX={4} paddingY={3} flexGrow={1} spacing={3}>
      <PageHeader
        title={
          <>
            <Text
              onClick={handleBackClick}
              variant="label"
              color="primary"
              fontWeight={500}
              sx={{
                cursor: "pointer",
                lineHeight: 1,
                fontSize: "13px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <ArrowBackIcon
                sx={{
                  color: colors.blue100,
                  width: "20px",
                  height: "20px",
                  marginRight: theme.spacing(0.5),
                }}
              />
              AI data extraction Checklist
            </Text>
            Review extracted data
          </>
        }
        subTitle={
          <Text variant="text2" color={colors.gray60}>
            Document category:{" "}
            <Text
              variant="text2"
              sx={{ fontWeight: 700, display: "inline" }}
              color={colors.black}
            >
              {isLoading ? (
                <Skeleton sx={{ display: "inline-block", width: "150px" }} />
              ) : (
                companySectionAutomaticFill.field.name
              )}
            </Text>
          </Text>
        }
      />

      <Stack direction="row" spacing={5}>
        {isLoading ? (
          <>
            <Skeleton width="60%" height={680} />
            <Skeleton width="40%" height={680} />
          </>
        ) : (
          <>
            <Stack spacing={1} width="60%">
              {companySectionAutomaticFill.files.length === 1 ? (
                <>
                  <Text variant="label">Source document:</Text>
                  <Text variant="h6">
                    {companySectionAutomaticFill.files[0].original_file_name}
                  </Text>
                </>
              ) : (
                <Select
                  size="small"
                  label="SOURCE DOCUMENT"
                  options={companySectionAutomaticFill.files.map((file) => ({
                    label: file.original_file_name,
                    value: file.id.toString(),
                  }))}
                  value={selectedDocumentId}
                  onChange={(e) =>
                    setSelectedDocumentId(Number(e.target.value))
                  }
                />
              )}
              <div
                style={{
                  zIndex: 2,
                  top: "130px",
                  position: "sticky",
                  height: "80vh",
                }}
              >
                <PDFEmbed
                  pdfFileUrl={
                    isFetchingCompanySectionAutomaticFillFileSignedUrl
                      ? undefined
                      : companySectionAutomaticFillFileSignedUrl
                  }
                  focusedQuote={PDFViewerFocusedQuote}
                  focusedQuoteClearanceCallback={() =>
                    setPDFViewerFocusedQuote(undefined)
                  }
                />
              </div>
            </Stack>

            <Stack spacing={3} width="40%">
              <Stack
                direction="row"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
              >
                <Stack spacing={0.5} flexGrow={1}>
                  <Text variant="text1" fontWeight={700}>
                    Extracted data
                  </Text>

                  <Text variant="text3">
                    {companySectionAutomaticFill.field_values.length} data
                    points extracted
                  </Text>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center">
                  {!isCompanySectionAutomaticFillFinished && (
                    <Button variant="outlined" onClick={handleBackClick}>
                      Review later
                    </Button>
                  )}
                  <Tooltip
                    title={
                      isCompanySectionAutomaticFillFinished
                        ? "Data has already been imported."
                        : undefined
                    }
                  >
                    <span>
                      <Button
                        onClick={handleApprove}
                        disabled={isCompanySectionAutomaticFillFinished}
                      >
                        Approve & move to deal data
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>

              <Stack spacing={2} flexGrow={1}>
                {companySectionAutomaticFill.field_values.map((fieldValue) => {
                  const isDisabled =
                    fieldsMarkedAsNA.includes(fieldValue.field.id) ||
                    isCompanySectionAutomaticFillFinished;
                  const sourceQuote = fieldValue.source_quote;
                  const showViewSource =
                    !_.isNil(sourceQuote) &&
                    !_.isNil(fieldValue.source_file_id);
                  const selectedFileMatchesSourceFile =
                    fieldValue.source_file_id === selectedDocumentId;
                  // No need to calculate the name if it's matching.
                  const sourceFileName = selectedFileMatchesSourceFile
                    ? ""
                    : companySectionAutomaticFill.files.find(
                        (file) => file.id === fieldValue.source_file_id
                      )?.original_file_name;
                  const transparentWhenDisabledStyles = {
                    opacity: isDisabled ? 0.4 : 1,
                    transition: "opacity 0.3s linear",
                  };

                  return (
                    <Stack
                      key={fieldValue.id}
                      padding={2}
                      sx={{
                        borderRadius: "8px",
                        backgroundColor: colors.blue5,
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        marginBottom={1}
                      >
                        <Text
                          variant="text2"
                          fontWeight={700}
                          sx={transparentWhenDisabledStyles}
                        >
                          {fieldValue.field.name}
                        </Text>

                        <Stack
                          spacing={0.5}
                          direction="row"
                          alignItems="center"
                        >
                          {showViewSource && (
                            <Tooltip
                              placement="top"
                              title={
                                selectedFileMatchesSourceFile
                                  ? undefined
                                  : `Please switch to ${sourceFileName} to view source.`
                              }
                            >
                              <Text
                                variant="text3"
                                color={colors.blue120}
                                sx={{
                                  textWrap: "nowrap",
                                  cursor: selectedFileMatchesSourceFile
                                    ? "pointer"
                                    : "not-allowed",
                                  color: selectedFileMatchesSourceFile
                                    ? colors.blue120
                                    : colors.gray60,
                                }}
                                onClick={
                                  selectedFileMatchesSourceFile
                                    ? () =>
                                        setPDFViewerFocusedQuote(sourceQuote)
                                    : undefined
                                }
                              >
                                View source
                              </Text>
                            </Tooltip>
                          )}

                          {!isCompanySectionAutomaticFillFinished && (
                            <ButtonDropdown
                              icon={<MoreVertIcon />}
                              label=""
                              variant="text"
                              color="secondary"
                              width="auto"
                              sx={{
                                height: "23px",
                                padding: theme.spacing(0, 0.75),
                                "& .MuiButton-endIcon": {
                                  margin: 0,
                                },
                              }}
                            >
                              <MenuItem
                                onClick={() =>
                                  handleFieldNAToggle({
                                    fieldId: fieldValue.field.id,
                                  })
                                }
                              >
                                {isDisabled
                                  ? "Revoke N/A status"
                                  : "Mark as N/A"}
                              </MenuItem>
                            </ButtonDropdown>
                          )}
                        </Stack>
                      </Stack>

                      <TextField
                        size="small"
                        disabled={isDisabled}
                        defaultValue={reviewedData[fieldValue.field.id]}
                        sx={{
                          backgroundColor: colors.white,
                          ...transparentWhenDisabledStyles,
                        }}
                        onChange={(e) =>
                          setReviewedData((prevData) => ({
                            ...prevData,
                            [fieldValue.field.id]: e.target.value,
                          }))
                        }
                      />
                    </Stack>
                  );
                })}
              </Stack>
            </Stack>
          </>
        )}
      </Stack>
      <style>{`#freshworks-container { display:none; }`}</style>
    </Stack>
  );
};

export default DealPackageExtractedDataReview;
